/**
 * DebugLayoutHud — the layout debug controls (recompute / animate / pause /
 * step / debug overlay / refresh), rendered as tiny chips docked on the
 * minimap alongside the layout-switch toggles (`DevSwitchesHud`). Dev-only:
 * the caller gates on `IS_DEV` and passes it as the board's `minimapExtras`.
 *
 * These used to sit in the main board HUD toolbar, intermixed with the proper
 * controls (view picker, colour-by, aspect…). Moving them to the tiny minimap
 * pill keeps debug visually distinct from primary controls.
 */
import { useTranslation } from "../../i18n";
import { miniChipClass } from "./miniChip";

export interface DebugLayoutHudProps {
  readonly onRecompute: () => void;
  readonly onAnimate: () => void;
  readonly animating: boolean;
  readonly animPaused: boolean;
  readonly onToggleAnimPause: () => void;
  readonly onStep: () => void;
  readonly debugLayout: boolean;
  readonly onToggleDebug: () => void;
  readonly onRefresh: () => void;
}

export function DebugLayoutHud(props: DebugLayoutHudProps) {
  const { t } = useTranslation("workbench");
  return (
    <div
      className="flex flex-wrap justify-end gap-1"
      data-hud-region="debug-layout"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={miniChipClass(false)}
        title={t("recomputeTitle")}
        onClick={props.onRecompute}
      >
        {t("recompute")}
      </button>
      <button
        type="button"
        className={miniChipClass(props.animating, "emerald")}
        title={t("animateTitle")}
        disabled={props.animating}
        onClick={props.onAnimate}
      >
        {props.animating ? t("animating") : t("animate")}
      </button>
      {props.animating && (
        <button
          type="button"
          className={miniChipClass(true, "amber")}
          title={props.animPaused ? t("resumeTitle") : t("pauseTitle")}
          onClick={props.onToggleAnimPause}
        >
          {props.animPaused ? t("resume") : t("pause")}
        </button>
      )}
      {props.animating && props.animPaused && (
        <button
          type="button"
          className={miniChipClass(true, "sky")}
          title={t("stepTitle")}
          onClick={props.onStep}
        >
          {t("step")}
        </button>
      )}
      <button
        type="button"
        className={miniChipClass(props.debugLayout, "amber")}
        title={t("debugTitle")}
        onClick={props.onToggleDebug}
      >
        {props.debugLayout ? t("debugOn") : t("debugOff")}
      </button>
      <button
        type="button"
        className={miniChipClass(false)}
        title={t("refresh")}
        onClick={props.onRefresh}
      >
        {t("refresh")}
      </button>
    </div>
  );
}
